/** RFC 0345: no project surface mounts before its personal World is verified. */

import type { AnyDocumentId } from '@automerge/automerge-repo/slim'
import type { WorldDoc } from '@mod/core/model'
import type { openPersonalWorld } from '@mod/core/sharing'
import { type ReactNode, useEffect, useRef, useState } from 'react'
import {
  createSharingAccountClient,
  type SharingAccount,
  startSharingSignIn,
} from '../../services/sharing/account'
import {
  type OpenSharingAccount,
  SharingAccountContext,
  useSharingAccount,
} from '../../services/sharing/accountContext'
import {
  type PasskeySupport,
  passkeySetupError,
  passkeySupport,
} from '../../services/sharing/passkeySupport'
import {
  clearPendingInvite,
  pendingInvite,
} from '../../services/sharing/pendingInvite'
import {
  stuckMessage,
  useSyncHealth,
} from '../../services/sharing/useSyncHealth'
import { useTabRepoBoot } from '../../services/world/repo/TabRepoProvider'
import { getOrCreateWorld } from '../../services/world/world'

type Opened = Awaited<ReturnType<typeof openPersonalWorld>>
export const useSharingAccountWorld = () => useSharingAccount()?.opened

/** How often a device looks for guests who used one of its person's invites. */
const SETTLE_MS = 3000
/** How often this window looks for invites granted to its person (S16). */
const MINE_MS = 30_000

export function SharingAccountGate({ children }: { children: ReactNode }) {
  const boot = useTabRepoBoot()
  if (!boot.sharing) return <>{children}</>
  return <AccountGate>{children}</AccountGate>
}

function Notice({
  role,
  children,
  onClose,
}: {
  role: 'status' | 'alert'
  children: ReactNode
  onClose: () => void
}) {
  return (
    <div
      role={role}
      className="fixed top-3 left-1/2 z-50 -translate-x-1/2 rounded-md border border-border bg-background px-3 py-1.5 text-sm shadow"
    >
      {children}{' '}
      <button type="button" className="text-muted-foreground" onClick={onClose}>
        Close
      </button>
    </div>
  )
}

function AccountGate({ children }: { children: ReactNode }) {
  const { sharing, repo } = useTabRepoBoot()
  const authServer = import.meta.env.VITE_MOD_AUTH_URL
  const [client] = useState(() =>
    authServer ? createSharingAccountClient(authServer) : undefined,
  )
  const [account, setAccount] = useState<SharingAccount>()
  const [open, setOpen] = useState<OpenSharingAccount>()
  const [setupState, setSetupState] = useState<
    'new' | 'resume' | 'recovery-required' | 'ready'
  >()
  const [busy, setBusy] = useState(true)
  const [error, setError] = useState('')
  const [joined, setJoined] = useState('')
  const [deviceJoined, setDeviceJoined] = useState('')
  const [joining, setJoining] = useState(false)
  const joinAbort = useRef<AbortController | null>(null)
  // RFC 0345 3a: asked before setup makes a World, so a window that cannot
  // make the recovery passkey offers the browser-then-join path instead.
  const [passkeys, setPasskeys] = useState<PasskeySupport>()
  // RFC 0345: Keyhive events this tab cannot apply, said once rather than
  // left as a silent stall. Closing it hides this message until it changes.
  const stuck = stuckMessage(useSyncHealth())
  const [dismissedStuck, setDismissedStuck] = useState<string | null>(null)
  const invite = pendingInvite()

  async function finish(opened: Opened, who: SharingAccount) {
    await getOrCreateWorld(repo)
    const pending = pendingInvite()
    if (pending && client) {
      try {
        const { acceptPendingInvite } = await import(
          '../../services/sharing/invites'
        )
        const target = await acceptPendingInvite({
          sharing: sharing!,
          client,
          person: opened.person,
          world: opened.world as never,
          invite: pending,
        })
        sharing!.requestDocument(target.documentId)
        setJoined(target.title)
      } catch (e) {
        clearPendingInvite()
        setError(e instanceof Error ? e.message : 'Could not accept the invite')
      }
    }
    setOpen({ opened, account: who, client: client! })
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: once per client; finish reads current state
  useEffect(() => {
    let alive = true
    void (async () => {
      try {
        if (!client) throw new Error('Account sign-in is not configured')
        const who = await client.account()
        if (alive) setAccount(who)
        const state = await sharing!.accountSetupState(who.id, client)
        if (state === 'new' || state === 'resume') {
          const support = await passkeySupport()
          if (alive) setPasskeys(support)
        }
        if (alive) setSetupState(state)
        if (state === 'ready') {
          const world = await sharing!.openAccount(who.id)
          if (world && alive) await finish(world, who)
        }
      } catch (e) {
        if (alive) setError(passkeySetupError(e))
      } finally {
        if (alive) setBusy(false)
      }
    })()
    return () => {
      alive = false
    }
  }, [client, sharing, repo])

  // Settle sent invites while this window is open (3h, 3j).
  useEffect(() => {
    if (!open || !sharing) return
    let running = false
    const tick = async () => {
      if (running) return
      running = true
      try {
        const world = await repo.find<WorldDoc>(
          open.opened.location.worldId as AnyDocumentId,
        )
        const { settleInvites } = await import('../../services/sharing/invites')
        await settleInvites(sharing, world, open.client)
      } catch (e) {
        console.warn('[sharing] settling invites failed', e)
      } finally {
        running = false
      }
    }
    void tick()
    const timer = setInterval(() => void tick(), SETTLE_MS)
    // S16: invites already granted to this person list without the link.
    let listing = false
    const listMine = async () => {
      if (listing) return
      listing = true
      try {
        const world = await repo.find<WorldDoc>(
          open.opened.location.worldId as AnyDocumentId,
        )
        const { listGrantedInvites } = await import(
          '../../services/sharing/invites'
        )
        await listGrantedInvites(world, open.client)
      } catch (e) {
        console.warn('[sharing] listing invites failed', e)
      } finally {
        listing = false
      }
    }
    void listMine()
    const mine = setInterval(() => void listMine(), MINE_MS)
    // S16: so others can invite this person straight into their session list.
    void (async () => {
      try {
        const { personEvents } = await import('@mod/core/sharing')
        await open.client.publishPersonEvents(
          await personEvents(sharing, open.opened.person),
        )
      } catch (e) {
        console.warn('[sharing] publishing this person failed', e)
      }
    })()
    return () => {
      clearInterval(timer)
      clearInterval(mine)
    }
  }, [open, sharing, repo])

  useEffect(() => {
    if (!open || !sharing) return
    return sharing.startApprovingEnrollments(
      open.client,
      open.account.id,
      setDeviceJoined,
    )
  }, [open, sharing])

  // RFC 0345 A4: each message this person posts is pledged to their own
  // account, so a turn answering it on Mod's model bills them, not the host.
  useEffect(() => {
    if (!open || !sharing) return
    let undo = () => {}
    let live = true
    void import('@mod/core/sharing').then(({ afterSessionPosts }) => {
      if (!live) return
      undo = afterSessionPosts(sharing.repo, (sessionId, promptId) => {
        void open.client
          .pledgePrompt({ sessionId, promptId })
          .catch((e) => console.warn('[sharing] pledge failed', e))
      })
    })
    return () => {
      live = false
      undo()
    }
  }, [open, sharing])

  useEffect(() => () => joinAbort.current?.abort(), [])

  async function setup() {
    if (!client || busy || !account) return
    setBusy(true)
    setError('')
    try {
      const support = passkeys ?? (await passkeySupport())
      if (!support.ok) {
        setPasskeys(support)
        return
      }
      await finish(await sharing!.setupAccount(client), account)
    } catch (e) {
      setError(passkeySetupError(e))
    } finally {
      setBusy(false)
    }
  }
  async function joinDevice() {
    if (!client || busy || !account) return
    const controller = new AbortController()
    joinAbort.current = controller
    setJoining(true)
    setBusy(true)
    setError('')
    try {
      await finish(
        await sharing!.joinAccount(
          client,
          account.id,
          controller.signal,
          sharing!.requestDocument,
        ),
        account,
      )
    } catch (e) {
      if (!controller.signal.aborted)
        setError(e instanceof Error ? e.message : 'Could not join this device')
    } finally {
      joinAbort.current = null
      setJoining(false)
      setBusy(false)
    }
  }
  if (open)
    return (
      <SharingAccountContext.Provider value={open}>
        {joined && (
          <Notice role="status" onClose={() => setJoined('')}>
            You joined {joined}.
          </Notice>
        )}
        {!joined && !error && deviceJoined && (
          <Notice role="status" onClose={() => setDeviceJoined('')}>
            A new device joined: {deviceJoined}
          </Notice>
        )}
        {error && (
          <Notice role="alert" onClose={() => setError('')}>
            {error}
          </Notice>
        )}
        {/* One notice at a time: they share the same spot at the top. */}
        {!joined &&
          !deviceJoined &&
          !error &&
          stuck &&
          stuck !== dismissedStuck && (
            <Notice role="alert" onClose={() => setDismissedStuck(stuck)}>
              {stuck}
            </Notice>
          )}
        {children}
      </SharingAccountContext.Provider>
    )
  return (
    <main className="flex min-h-screen items-center justify-center p-8">
      <section className="w-full max-w-md space-y-4">
        <h1 className="font-semibold text-xl">
          {invite ? 'Sign in to accept your invite' : 'Open your Mod account'}
        </h1>
        <p className="text-sm text-muted-foreground">
          {invite
            ? 'Sign in as the address the invite was sent to.'
            : 'Sign in to open your private World. A recovery passkey protects access if you lose your devices.'}
        </p>
        {error && (
          <p role="alert" className="text-sm">
            {error}
          </p>
        )}
        {busy ? (
          <>
            <p role="status">
              {joining ? 'Waiting for an enrolled device…' : 'Opening account…'}
            </p>
            {joining && (
              <button
                type="button"
                className="rounded border px-4 py-2"
                onClick={() => joinAbort.current?.abort()}
              >
                Cancel
              </button>
            )}
          </>
        ) : (
          <>
            <button
              type="button"
              className="rounded border px-4 py-2"
              disabled={!client}
              onClick={() => {
                if (authServer) startSharingSignIn(authServer)
              }}
            >
              {account ? 'Sign in as someone else' : 'Sign in with Google'}
            </button>
            {account && (
              <>
                {(setupState === 'new' || setupState === 'resume') &&
                  passkeys &&
                  !passkeys.ok && (
                    <>
                      <p role="alert" className="text-sm">
                        {passkeys.message}
                      </p>
                      {passkeys.reason === 'desktop' && (
                        <button
                          type="button"
                          className="rounded border px-4 py-2"
                          onClick={() =>
                            // The shell hands an `external` window.open to
                            // the system browser.
                            window.open(
                              window.location.href,
                              '_blank',
                              'noopener,noreferrer,external',
                            )
                          }
                        >
                          Set up in your browser
                        </button>
                      )}
                    </>
                  )}
                {(setupState === 'new' || setupState === 'resume') &&
                  passkeys?.ok !== false && (
                    <button
                      type="button"
                      className="rounded border px-4 py-2"
                      onClick={() => void setup()}
                    >
                      {setupState === 'resume'
                        ? 'Resume account setup'
                        : 'Set up this account'}
                    </button>
                  )}
                {setupState === 'recovery-required' && (
                  <button
                    type="button"
                    className="rounded border px-4 py-2"
                    onClick={() => void joinDevice()}
                  >
                    Join this device
                  </button>
                )}
                <p className="text-sm text-muted-foreground">
                  Signed in as {account.email}.{' '}
                  {setupState === 'recovery-required'
                    ? 'An enrolled device must be online to approve this one. If none remain, passkey recovery is not available in this preview yet.'
                    : setupState === 'resume'
                      ? 'Resume setup with your original World and recovery passkey.'
                      : setupState === 'new'
                        ? 'Set up your private World and recovery passkey.'
                        : 'Account setup could not be verified. Sign in again to retry.'}
                </p>
              </>
            )}
          </>
        )}
      </section>
    </main>
  )
}
